import { emailTagTransform } from '@ps/web-core-utils';
import { useTrackers } from '@ps/web-shared-analytics';
import { useTranslationValues } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import { camelCase } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { renderToStaticMarkup, renderToString } from 'react-dom/server';
import { useDispatch, useSelector } from 'react-redux';

import type { StatusCode } from '@swf/core/enums/statusCode';
import { getState, select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { statusKey } from '@swf/core/utils';

const RESOLVE_REGEX =
  /(?::\/\/resolve\/|\[#@?)([^#>'"\]]+?(?:\.[^#>'"\]]+)*)(#\])?\s*(?=[#>\]'"#.]| |$)/g;
const DOMAIN_REGEX = /[a-z]+\.[a-z]{2,3}$/;
const HREF_REGEX = /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/;
const LINK_REGEX = /<a\s*(.*)>(.*)<\/a>/;
const URL_REGEX = /(www|http:|https:|mailto:)+[^\s]+[\w]/;

const renderLink = (href: string, isEmbedded: boolean, base: string, children: string) =>
  renderToString(
    <StarsLink
      target="_blank"
      rel="noopener noreferrer"
      href={isEmbedded ? `external-${new URL(href, base).toString()}` : href}
    >
      {children}
    </StarsLink>
  );

const ServerMessageHandler: React.FC<{ message: string; code?: StatusCode }> = ({
  message,
  code
}) => {
  const values = useTranslationValues();
  const { errorViewed } = useTrackers();
  const isEmbedded = useSelector(select.device.isEmbedded);
  const base = useSelector(select.location.getOrigin);
  const [resolvedUrl, setResolvedUrl] = useState('');
  const containsLink = message.match(HREF_REGEX);
  const needsResolving = useMemo(() => RESOLVE_REGEX.test(message), [message]);
  const [, , link] = containsLink || [];
  const {
    resolver: { fetchTag }
  } = useDispatch<Dispatch>();

  const fetchUrl = useCallback(
    async (tag: string) => {
      try {
        await fetchTag({ tag });
        const rootState = getState();
        const { url } = select.resolver.getTag(rootState, tag);
        setResolvedUrl(url);
      } catch {
        // do nothing
      }
    },
    [fetchTag]
  );

  const html = useMemo(
    () =>
      !needsResolving
        ? message
        : message.replace(RESOLVE_REGEX, (match, value) => {
            let key: string;
            let domain: string | null = null;

            const parts = value.split('-');
            if (value.includes(':') || value.includes('?')) {
              key = camelCase(value.split(':'));
            } else {
              const hasDomain = DOMAIN_REGEX.test(value);
              if (hasDomain) {
                domain = parts.splice(-1);
                key = camelCase(parts.concat('Starsdomain'));
              } else if (!value.includes('starsdomain')) {
                parts.splice(-1);
                key = camelCase(parts.concat('Starsdomain'));
              } else {
                key = camelCase(parts);
              }
            }

            const { emailText, emailLink } = emailTagTransform(
              key,
              null,
              domain,
              values.appStarsdomain as string,
              StarsLink
            );
            if (emailText) {
              return emailText;
            }
            if (emailLink) {
              return renderToStaticMarkup(emailLink);
            }

            if (resolvedUrl) {
              return resolvedUrl;
            }

            return values[key] as string;
          }),
    [needsResolving, message, values, resolvedUrl]
  );

  const formattedHtml = useMemo(() => {
    if (!LINK_REGEX.test(html) || URL_REGEX.test(link)) {
      return html;
    } else {
      const [searchValue, , text] = LINK_REGEX.exec(html) || [];
      const [, , url] = html.match(HREF_REGEX) || [];
      return searchValue && text && url && resolvedUrl
        ? html.replace(searchValue, renderLink(url, isEmbedded, base, text))
        : html;
    }
  }, [html, base, isEmbedded, link, resolvedUrl]);

  useEffect(() => {
    if (containsLink && !resolvedUrl) {
      if (!URL_REGEX.test(link)) {
        const [, resolver] = RESOLVE_REGEX.exec(link) || [];
        resolver && fetchUrl(resolver);
      }
    }
  }, [resolvedUrl, message, containsLink, fetchUrl, link]);

  useEffect(() => {
    code && errorViewed({ errorCode: statusKey(code) });
  }, [errorViewed, code]);

  return <span dangerouslySetInnerHTML={{ __html: formattedHtml }} />;
};

export default ServerMessageHandler;
